import React, { useState, useEffect, useCallback } from 'react';
import shufflrLogo from './assets/logo.svg';
import { TenantProvider, useTenant } from './context/TenantContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingPage } from './pages/BrandingPage';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { AdminPage } from './pages/AdminPage';
import { StandingsPage } from './pages/StandingsPage';
import { MyMatchesPage } from './pages/MyMatchesPage';
import { PlayoffPage } from './pages/PlayoffPage';
import { KeyTag } from './components/KeyTag';
import type { KeyTagVariant } from './components/KeyTag';
import { apiFetch } from './lib/apiFetch';
import {
  appShellStyle,
  headerStyle,
  headerBrandStyle,
  headerLogoStyle,
  headerTitleStyle,
  headerTaglineStyle,
  headerNavStyle,
  navBtnStyle,
  navBtnActiveStyle,
  pageStyle,
  headingStyle,
  mutedTextStyle,
  errorBoxStyle,
  retryBtnStyle,
  cardStyle,
  cardHeaderRowStyle,
  cardTitleStyle,
  leagueFormatTagStyle,
  cardHintStyle,
  matchCardStyle,
  matchCourtStripStyle,
  matchBodyStyle,
  matchTeamsRowStyle,
  matchTeamNameStyle,
  matchScoreSepStyle,
  matchMetaRowStyle,
  backBtnStyle,
  badgeStyle,
  statusColor,
  headerUserStyle,
  navLogoutStyle,
  scoreActionWrapStyle,
  submitScoreBtnStyle,
  scoreFormRowStyle,
  scoreInputStyle,
  scoreInputLabelStyle,
  scoreVsSepStyle,
  approveScoreBtnStyle,
  rejectScoreBtnStyle,
  scoreActionErrorStyle,
  pendingBannerStyle,
standingsTableStyle,
standingsThStyle,
standingsTdStyle,
standingsRankCellStyle,
standingsWinPctStyle,
standingsRowStyle,
standingsRowTopStyle,
bracketWrapStyle,
bracketRoundColStyle,
bracketRoundTitleStyle,
bracketMatchCardStyle,
bracketTeamRowStyle,
bracketTeamNameStyle,
bracketScoreStyle,
bracketWinnerStyle,
bracketByeStyle,
bracketSeedBtnStyle,
bracketSeedFormStyle,
bracketSeedRowStyle,
bracketSeedLabelStyle,
bracketSeedInputStyle,
bracketSeedSelectStyle,
bracketStatusBadgeStyle,
} from './App.styles';

// ─── Types ────────────────────────────────────────────────────────────────────────────

interface League {
  id: string;
  name: string;
  format: string;
  organizationId: string;
}

interface Match {
  id: string;
  scheduledAt: string;
  status: string;
  teamA?: { name?: string };
  teamB?: { name?: string };
  court?: { name?: string; id?: string };
  result?: { scoreA: number; scoreB: number; status: string } | null;
}

// ─── Navigation ──────────────────────────────────────────────────────────────────────

function usePage(): string {
  const [page, setPage] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPage(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return page;
}

function navigate(path: string): void {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// ─── Header ──────────────────────────────────────────────────────────────────────────

function AppHeader({ page, orgPrefix }: { page: string; orgPrefix: string }): React.JSX.Element {
  const { org } = useTenant();
  const { user, logout } = useAuth();
  const isAdmin = !!user?.roles?.some((r: string) => ['ORG_ADMIN', 'LEAGUE_ADMIN', 'SUPER_ADMIN'].includes(r));

  const navBtn = (label: string, path: string): React.JSX.Element => (
    <button
      key={path}
      onClick={() => navigate(path)}
      style={page === path ? navBtnActiveStyle : navBtnStyle}
    >
      {label}
    </button>
  );

  return (
    <header style={headerStyle}>
      <div style={headerBrandStyle}>
        {org?.theme.logoUrl ? (
          <img src={org.theme.logoUrl} alt={org.name + ' logo'} style={headerLogoStyle} />
        ) : (
          <img
            src={shufflrLogo}
            alt="Shufflr"
            style={{ height: 32, width: 'auto', filter: 'brightness(0) invert(1)', opacity: 0.95 }}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <span style={headerTitleStyle}>{org ? org.name : 'Shuffleboard Club League'}</span>
          <span style={headerTaglineStyle}>Good Games · Great Vibes</span>
        </div>
      </div>
      <nav style={headerNavStyle}>
        {navBtn('Leagues', '/')}
      {navBtn('Standings', '/standings')}
{navBtn('Playoffs', '/playoffs')}
      {user && navBtn('My Matches', '/my-matches')}
        {user && navBtn('Branding', '/branding')}
      {user && isAdmin && navBtn('Admin', '/admin')}
        {user ? (
          <>
            <span style={headerUserStyle}>{user.email}</span>
            <button style={navLogoutStyle} onClick={() => { logout(); navigate(orgPrefix + '/login'); }}>
              Sign out
            </button>
          </>
        ) : (
          <button style={navBtnStyle} onClick={() => navigate(orgPrefix + '/login')}>
            Sign in
          </button>
        )}
      </nav>
    </header>
  );
}

// ─── Leagues page ───────────────────────────────────────────────────────────────────────

function LeaguesPage(): React.JSX.Element {
  const { slug } = useTenant();
  const { token } = useAuth();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/leagues', { slug, token });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setLeagues(await res.json());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load leagues');
    } finally {
      setLoading(false);
    }
  }, [slug, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={mutedTextStyle}>Loading leagues…</p>;

  return (
    <main style={pageStyle}>
      <h1 style={headingStyle}>Leagues</h1>

      {error && (
        <div role="alert" style={errorBoxStyle}>
          {error}{' '}
          <button onClick={load} style={retryBtnStyle}>Retry</button>
        </div>
      )}

      {!error && leagues.length === 0 && (
        <p style={mutedTextStyle}>No leagues found for this organisation.</p>
      )}

      {leagues.map((league, idx) => (
        <div
          key={league.id}
          style={cardStyle}
          onClick={() => navigate('/leagues/' + league.id + '/matches')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/leagues/' + league.id + '/matches')}
        >
          <div style={cardHeaderRowStyle}>
            <KeyTag
              number={idx + 1}
              variant={idx % 2 === 0 ? 'pink' : 'green'}
              size="sm"
              label={`League ${idx + 1}`}
            />
            <span style={cardTitleStyle}>{league.name}</span>
            <span style={leagueFormatTagStyle}>{league.format.replace(/_/g, ' ')}</span>
          </div>
          <p style={cardHintStyle}>Tap to view match schedule →</p>
        </div>
      ))}
    </main>
  );
}

// ─── Match schedule page ───────────────────────────────────────────────────────────────

function courtVariant(court?: { name?: string; id?: string }): KeyTagVariant {
  const seed = court?.id ?? court?.name ?? '';
  const sum = seed.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return sum % 2 === 0 ? 'pink' : 'green';
}

function MatchSchedulePage({ leagueId }: { leagueId: string }): React.JSX.Element {
  const { slug } = useTenant();
  const { user, token } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openSubmitId, setOpenSubmitId] = useState<string | null>(null);
  const [scoreA, setScoreA] = useState<number>(0);
  const [scoreB, setScoreB] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const isAdmin = !!user?.roles?.some((r: string) =>
    ['ORG_ADMIN', 'LEAGUE_ADMIN', 'SUPER_ADMIN'].includes(r)
  );

  const load = useCallback(async () => {
    if (!slug) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/matches?leagueId=' + leagueId, { slug, token });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setMatches(await res.json());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  }, [slug, token, leagueId]);

  useEffect(() => { load(); }, [load]);

  const openSubmit = (matchId: string) => {
    setOpenSubmitId(matchId);
    setScoreA(0);
    setScoreB(0);
    setSubmitError(null);
  };

  const cancelSubmit = () => { setOpenSubmitId(null); setSubmitError(null); };

  const handleSubmitScore = async (matchId: string) => {
    if (!token) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const res = await apiFetch(`/api/v1/matches/${matchId}/submit-score`, {
        method: 'POST',
        slug,
        token,
        body: JSON.stringify({ scoreA, scoreB }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.message || 'HTTP ' + res.status); }
      setOpenSubmitId(null); await load();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit score');
    } finally { setSubmitting(false); }
  };

  const handleApprove = async (matchId: string) => {
    if (!token) return;
    setActioningId(matchId); setActionError(null);
    try {
      const res = await apiFetch(`/api/v1/matches/${matchId}/approve-score`, {
        method: 'POST',
        slug,
        token,
        body: JSON.stringify({}),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.message || 'HTTP ' + res.status); }
      await load();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve');
    } finally { setActioningId(null); }
  };

  const handleReject = async (matchId: string) => {
    if (!token) return;
    setActioningId(matchId); setActionError(null);
    try {
      const res = await apiFetch(`/api/v1/matches/${matchId}/reject-score`, {
        method: 'POST',
        slug,
        token,
        body: JSON.stringify({}),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.message || 'HTTP ' + res.status); }
      await load();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to reject');
    } finally { setActioningId(null); }
  };

  const formatDate = (iso: string): string => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      }).format(new Date(iso));
    } catch { return iso; }
  };

  return (
    <main style={pageStyle}>
      <button onClick={() => navigate('/')} style={backBtnStyle}>
        ← Back to Leagues
      </button>
      <h1 style={headingStyle}>Match Schedule</h1>
      {loading && <p style={mutedTextStyle}>Loading matches…</p>}
      {error && (
        <div role="alert" style={errorBoxStyle}>
          {error}{' '}
          <button onClick={load} style={retryBtnStyle}>Retry</button>
        </div>
      )}
      {!loading && !error && matches.length === 0 && (
        <p style={mutedTextStyle}>No matches scheduled yet.</p>
      )}
      {matches.map((match) => {
        const scoreLabel = match.result
          ? `${match.result.scoreA} – ${match.result.scoreB}`
          : 'vs';
        const resultStatus = match.result?.status ?? null;
        const variant = courtVariant(match.court);
        const isSubmitOpen = openSubmitId === match.id;
        const isActioning = actioningId === match.id;
        return (
          <div key={match.id} style={matchCardStyle}>
            <div style={matchCourtStripStyle}>
              <KeyTag
                number={match.court?.id ? parseInt(match.court.id.replace(/\D/g, ''), 10) || 1 : 1}
                variant={variant}
                size="sm"
                label={match.court?.name ?? 'Court'}
              />
            </div>
            <div style={matchBodyStyle}>
              <div style={matchTeamsRowStyle}>
                <span style={matchTeamNameStyle}>{match.teamA?.name ?? 'TBA'}</span>
                <span style={matchScoreSepStyle}>{scoreLabel}</span>
                <span style={matchTeamNameStyle}>{match.teamB?.name ?? 'TBA'}</span>
                <span style={badgeStyle(statusColor[match.status] ?? '#6b7280')}>
                  {match.status}
                </span>
                {resultStatus && (
                  <span style={badgeStyle(statusColor[resultStatus] ?? '#6b7280')}>
                    {resultStatus}
                  </span>
                )}
              </div>
              <div style={matchMetaRowStyle}>
                <span>{formatDate(match.scheduledAt)}</span>
                {match.court?.name && <span>{match.court.name}</span>}
              </div>
              {match.status === 'SCHEDULED' && user && (
                <div style={scoreActionWrapStyle}>
                  {!isSubmitOpen ? (
                    <button style={submitScoreBtnStyle} onClick={() => openSubmit(match.id)}>
                      Submit Score
                    </button>
                  ) : (
                    <div>
                      <div style={scoreFormRowStyle}>
                        <span style={scoreInputLabelStyle}>{match.teamA?.name ?? 'Team A'}</span>
                        <input type="number" min={0} max={99} value={scoreA}
                          onChange={(e) => setScoreA(Number(e.target.value))}
                          style={scoreInputStyle} />
                        <span style={scoreVsSepStyle}>–</span>
                        <input type="number" min={0} max={99} value={scoreB}
                          onChange={(e) => setScoreB(Number(e.target.value))}
                          style={scoreInputStyle} />
                        <span style={scoreInputLabelStyle}>{match.teamB?.name ?? 'Team B'}</span>
                      </div>
                      {submitError && <p style={scoreActionErrorStyle}>{submitError}</p>}
                      <div style={{ ...scoreFormRowStyle, marginTop: 10 }}>
                        <button style={approveScoreBtnStyle}
                          onClick={() => handleSubmitScore(match.id)}
                          disabled={submitting}>
                          {submitting ? 'Submitting…' : 'Submit'}
                        </button>
                        <button style={rejectScoreBtnStyle} onClick={cancelSubmit} disabled={submitting}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {resultStatus === 'PENDING' && isAdmin && (
                <div style={scoreActionWrapStyle}>
                  <span style={pendingBannerStyle}>⏳ Score pending review</span>
                  {actionError && <p style={scoreActionErrorStyle}>{actionError}</p>}
                  <div style={{ ...scoreFormRowStyle, marginTop: 8 }}>
                    <button style={approveScoreBtnStyle}
                      onClick={() => handleApprove(match.id)} disabled={isActioning}>
                      {isActioning ? 'Approving…' : '✓ Approve'}
                    </button>
                    <button style={rejectScoreBtnStyle}
                      onClick={() => handleReject(match.id)} disabled={isActioning}>
                      {isActioning ? 'Rejecting…' : '✗ Reject'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </main>
  );
}

// ─── Router ────────────────────────────────────────────────────────────────────────────

function AppContent(): React.JSX.Element {
  const page = usePage();
  const { slug } = useTenant();
  const { user, logout } = useAuth();
  const isAdmin = !!user?.roles?.some((r: string) => ['ORG_ADMIN', 'LEAGUE_ADMIN', 'SUPER_ADMIN'].includes(r));
  const [authView, setAuthView] = useState<'login' | 'register'>('login');

  // Prefix for org path-based routing (empty when using subdomain).
  const orgPrefix = slug && !/^[^.]+\.[^.]+\.[^.]+/.test(window.location.hostname)
    ? '/org/' + slug
    : '';

  // Listen for token-expiry events emitted by apiFetch on 401 responses.
  useEffect(() => {
    const onExpired = () => {
      logout();
      navigate(orgPrefix + '/login');
    };
    window.addEventListener('shufflr:auth-expired', onExpired);
    return () => window.removeEventListener('shufflr:auth-expired', onExpired);
  }, [logout, orgPrefix]);

  // Bare domain → landing page.
  if (!slug) {
    return <LandingPage />;
  }

  // Auth routes — always accessible when not logged in.
  const authPage = page === '/login' || page.endsWith('/login')
    || page === '/register' || page.endsWith('/register');

  if (authPage || (!user && (page === '/' || page.endsWith('/')))) {
    // Already logged in hitting /login → redirect home.
    if (user && authPage) {
      navigate('/');
      return <></>;
    }
    const view = (page === '/register' || page.endsWith('/register')) ? 'register' : authView;
    if (view === 'register') {
      return <RegisterPage onSwitchToLogin={() => { setAuthView('login'); navigate('/login'); }} />;
    }
    return <LoginPage onSwitchToRegister={() => { setAuthView('register'); navigate(orgPrefix + '/register'); }} />;
  }

  const leagueMatch = page.match(/^\/leagues\/([^/]+)\/matches$/);

  // Org app shell.
  let content: React.JSX.Element;
  if (leagueMatch) {
    content = <MatchSchedulePage leagueId={leagueMatch[1]} />;
  } else if (page === '/standings' || page.startsWith('/standings/')) {
  content = <StandingsPage />;
} else if (page === '/my-matches' || page.startsWith('/my-matches/')) {
  if (!user) { navigate(orgPrefix + '/login'); return <></>; }
  content = <MyMatchesPage />;
  } else if (page === '/playoffs' || page.startsWith('/playoffs/')) {
  content = <PlayoffPage />;
} else if (page === '/admin' || page.startsWith('/admin/')) {
  if (!user) { navigate(orgPrefix + '/login'); return <></>; }
  content = <AdminPage />;
  } else if (page === '/branding' || page.startsWith('/branding/')) {
    if (!user) { navigate(orgPrefix + '/login'); return <></>; }
    content = <BrandingPage />;
  } else {
    content = <LeaguesPage />;
  }

  return (
    <div style={appShellStyle}>
      <AppHeader page={page} orgPrefix={orgPrefix} />
      {content}
    </div>
  );
}

function App(): React.JSX.Element {
  return (
    <TenantProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;
